import React, { useEffect, useState, useCallback, useRef } from "react";
import { usePermissions } from "../../../PermissionsProvider";
import { EvaluationCriteriaService } from "../../../services/EvaluationCriteriaService";
import {
  AvailableCatalog,
  EvaluationCriteria,
  EvaluationType,
} from "../../../model_gen/evaluation";
import { Loader2Icon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Label } from "../../ui/label";
import ObjectiveCriteriaCard from "./ObjectiveCriteriaCard";
import SubjectiveCriteriaCard from "./SubjectiveCriteriaCard";
import CriteriaEditModal from "../../Criteria/CriteriaEditModal";

const EVAL_MODEL_OPTIONS = [
  { value: "__default__", label: "Application Default" },
  { value: "gpt-5.2", label: "GPT-5.2" },
  { value: "gpt-4.1", label: "GPT-4.1" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
  { value: "o3-mini", label: "o3-mini" },
  { value: "o3", label: "o3" },
];

interface TestRunEvaluationsProps {
  selectedCriteriaIds: number[];
  onCriteriaChange: (ids: number[]) => void;
  evalModel?: string;
  onEvalModelChange?: (model: string) => void;
}

const TestRunEvaluations: React.FC<TestRunEvaluationsProps> = ({
  selectedCriteriaIds,
  onCriteriaChange,
  evalModel,
  onEvalModelChange,
}) => {
  const { apiKey } = usePermissions();
  const [catalog, setCatalog] = useState<AvailableCatalog | null>(null);
  const [companyCriteria, setCompanyCriteria] = useState<EvaluationCriteria[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [activeFramework, setActiveFramework] = useState<string>("MAITAI_CLASSIC");
  const [thresholdEdits, setThresholdEdits] = useState<Record<number, number>>({});
  const initialDefaultApplied = useRef(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingCriteria, setEditingCriteria] =
    useState<EvaluationCriteria | null>(null);

  useEffect(() => {
    const sub1 = EvaluationCriteriaService.getAvailable(setLoading, apiKey).subscribe(
      (data: AvailableCatalog) => setCatalog(data),
      (err: any) => console.error("Failed to load catalog", err)
    );
    const sub2 = EvaluationCriteriaService.list(setLoading, apiKey).subscribe(
      (data: EvaluationCriteria[]) => {
        setCompanyCriteria(data);
        if (!initialDefaultApplied.current && selectedCriteriaIds.length === 0) {
          const exactMatch = data.find(
            (c) => c.type === EvaluationType.EXACT_MATCH && c.is_system
          );
          if (exactMatch?.id) {
            onCriteriaChange([exactMatch.id]);
            initialDefaultApplied.current = true;
          }
        }
      },
      (err: any) => console.error("Failed to load company criteria", err)
    );
    return () => {
      sub1.unsubscribe();
      sub2.unsubscribe();
    };
  }, [apiKey]);

  const toggleCriteria = (id: number) => {
    if (selectedCriteriaIds.includes(id)) {
      onCriteriaChange(selectedCriteriaIds.filter((x) => x !== id));
    } else {
      onCriteriaChange([...selectedCriteriaIds, id]);
    }
  };

  const handleEditCriteria = (crit: EvaluationCriteria) => {
    setEditingCriteria(crit);
    setModalOpen(true);
  };

  const handleAddCriteria = () => {
    setEditingCriteria(null);
    setModalOpen(true);
  };

  const handleModalSave = (saved: EvaluationCriteria) => {
    if (editingCriteria) {
      setCompanyCriteria((prev) =>
        prev.map((c) => (c.id === saved.id ? saved : c))
      );
    } else {
      setCompanyCriteria((prev) => [...prev, saved]);
      if (saved.id) {
        onCriteriaChange([...selectedCriteriaIds, saved.id]);
      }
    }
    setModalOpen(false);
    setEditingCriteria(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingCriteria(null);
  };

  const saveThreshold = useCallback(
    (criteriaId: number, threshold: number) => {
      EvaluationCriteriaService.update(
        criteriaId,
        { config: { threshold } },
        () => {},
        apiKey
      ).subscribe(
        (updated: EvaluationCriteria) => {
          setCompanyCriteria((prev) =>
            prev.map((c) => (c.id === updated.id ? updated : c))
          );
        },
        (err: any) => console.error("Failed to update threshold", err)
      );
    },
    [apiKey]
  );

  if (loading && !catalog) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2Icon className="w-6 h-6 animate-spin text-maitai-green" />
      </div>
    );
  }

  const deterministicCriteria = companyCriteria.filter(
    (c) => c.type && c.type !== EvaluationType.LLM_JUDGE
  );

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h3 className="text-lg font-gc-med text-foreground">
          Evaluation Criteria
        </h3>
        <p className="text-sm text-muted-foreground font-gc-reg">
          Select the criteria to evaluate test run results. Exact Match is
          always included by default.
        </p>
      </div>

      {onEvalModelChange && (
        <div className="space-y-1.5">
          <Label className="text-sm font-gc-med">Evaluation Model</Label>
          <p className="text-xs text-muted-foreground font-gc-reg">
            The model used to run LLM-judge criteria. Falls back to the
            application's configured model if not set.
          </p>
          <Select
            value={evalModel || "__default__"}
            onValueChange={(v) =>
              onEvalModelChange(v === "__default__" ? "" : v)
            }
          >
            <SelectTrigger className="w-60 h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVAL_MODEL_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <ObjectiveCriteriaCard
        deterministicCriteria={deterministicCriteria}
        selectedCriteriaIds={selectedCriteriaIds}
        catalog={catalog}
        toggleCriteria={toggleCriteria}
        thresholdEdits={thresholdEdits}
        setThresholdEdits={setThresholdEdits}
        saveThreshold={saveThreshold}
      />

      <SubjectiveCriteriaCard
        catalog={catalog}
        companyCriteria={companyCriteria}
        selectedCriteriaIds={selectedCriteriaIds}
        onCriteriaChange={onCriteriaChange}
        toggleCriteria={toggleCriteria}
        activeFramework={activeFramework}
        setActiveFramework={setActiveFramework}
        onEditCriteria={handleEditCriteria}
        onAddCriteria={handleAddCriteria}
      />

      <div className="text-sm text-muted-foreground font-gc-reg">
        {selectedCriteriaIds.length} criteria selected
      </div>

      <CriteriaEditModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        criteria={editingCriteria}
      />
    </div>
  );
};

export default TestRunEvaluations;
